import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { PlayerProvider, usePlayer } from './context/PlayerContext';
import { BusinessProvider } from './context/BusinessContext';
import { ThemeProvider } from './context/ThemeContext';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import MusicLibrary from './pages/MusicLibrary';
import PlaylistScheduler from './pages/PlaylistScheduler';
import AdminUsers from './pages/AdminUsers';
import BusinessManagement from './pages/BusinessManagement';
import Reports from './pages/Reports';
import PlaylistTemplates from './pages/PlaylistTemplates';
import TextToVoice from './pages/TextToVoice';
import Layout from './components/Layout';
import '@/App.css';

// Protected Route component for role-based access
const ProtectedRoute = ({ children, user, requiredRole }) => {
  if (requiredRole === 'super_admin' && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  if (requiredRole === 'admin' && !['super_admin', 'admin'].includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Inner App component that can use PlayerContext
const AppContent = () => {
  const { stopAutoMode } = usePlayer();
  
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = useCallback(() => {
    // Stop any playing audio first
    stopAutoMode();
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedBusinessId');
    setUser(null);
  }, [stopAutoMode]);

  return (
    <BrowserRouter>
      {user ? (
        <BusinessProvider user={user}>
          <Routes>
            <Route
              path="/"
              element={<Layout user={user} onLogout={handleLogout} />}
            >
              <Route index element={<Navigate to="/dashboard" />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route
                path="music"
                element={
                  <ProtectedRoute user={user} requiredRole="admin">
                    <MusicLibrary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="playlists"
                element={
                  <ProtectedRoute user={user} requiredRole="admin">
                    <PlaylistScheduler />
                  </ProtectedRoute>
                }
              />
              <Route
                path="users"
                element={
                  <ProtectedRoute user={user} requiredRole="super_admin">
                    <AdminUsers currentUser={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="businesses"
                element={
                  <ProtectedRoute user={user} requiredRole="super_admin">
                    <BusinessManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ProtectedRoute user={user} requiredRole="admin">
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="playlist-templates"
                element={
                  <ProtectedRoute user={user} requiredRole="super_admin">
                    <PlaylistTemplates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="text-to-voice"
                element={
                  <ProtectedRoute user={user} requiredRole="super_admin">
                    <TextToVoice />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Route>
          </Routes>
        </BusinessProvider>
      ) : (
        <Routes>
          <Route path="*" element={<Auth onLogin={handleLogin} />} />
        </Routes>
      )}
    </BrowserRouter>
  );
};

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <PlayerProvider>
          <AppContent />
        </PlayerProvider>
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
